import React, { useState, useEffect } from "react";
import api, { getFileUrl } from "../../../../api";
import {
 Card,
 Row,
 Col,
 Table,
 Badge,
 Modal,
 Button,
 ProgressBar,
} from "react-bootstrap";
import {
 PieChart,
 Pie,
 Cell,
 Tooltip,
 Legend,
 BarChart,
 Bar,
 XAxis,
 YAxis,
 CartesianGrid,
 LineChart,
 Line,
} from "recharts";
import { useParams } from "react-router-dom";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";


const COLORS = ["#007BFF", "#28A745", "#FFC107", "#DC3545", "#6C757D"];

const DeliveryTab = () => {
 const { id: workOrderId } = useParams();

 const [deliveries, setDeliveries] = useState([]);
 const [loading, setLoading] = useState(true);
 const [selectedDelivery, setSelectedDelivery] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [error, setError] = useState("");


 useEffect(() => {
 fetchDeliveries();
 }, [workOrderId]);

 const fetchDeliveries = async () => {
 try {
 setLoading(true);
 const res = await api.get("/deliveries", {
 params: { workOrder: workOrderId }},
 );

 if (!res.data.success) {
 setError("Failed to load deliveries");
 return;
 }

 const sorted = [...res.data.data].sort(
 (a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate)
 );

 setDeliveries(sorted);
 } catch (err) {
 console.error(err);
 setError("Error fetching deliveries");
 } finally {
 setLoading(false);
 }
 };

 // -----------------------
 // STATS & CHART DATA
 // -----------------------
 const total = deliveries.length;
 const pending = deliveries.filter(d => d.status === "Pending").length;
const received = deliveries.filter(d => d.status === "Received").length;
const inspected = deliveries.filter(d => d.status === "Inspected").length;
const accepted = deliveries.filter(d => d.status === "Accepted").length;
const rejected = deliveries.filter(d => d.status === "Rejected").length;

 const statusData = [
 { name: "Pending", value: pending },
 { name: "Received", value: received },
 { name: "Inspected", value: inspected },
 { name: "Accepted", value: accepted },
 { name: "Rejected", value: rejected },
];

 const vendorData = Object.entries(
 deliveries.reduce((acc, d) => {
 const vendor = d.vendor?.name || "Unknown Vendor";
 acc[vendor] = (acc[vendor] || 0) + 1;
 return acc;
 }, {})
 ).map(([vendor, count]) => ({ vendor, deliveries: count }));

 const monthlyTrend = Object.entries(
 deliveries.reduce((acc, d) => {
 const month = new Date(d.deliveryDate).toLocaleDateString("en-US", {
 month: "short",
 year: "numeric",
 });
 acc[month] = (acc[month] || 0) + 1;
 return acc;
 }, {})
 ).map(([month, count]) => ({ month, count }));


 // -----------------------
 // PDF ACTIONS
 // -----------------------
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/deliveries/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );

 window.open(fileURL, "_blank"); // preview only
 } catch (err) {
 console.error("PDF preview error:", err);
 alert("Failed to preview PDF");
 } finally {
 setPreviewingPdf(false);
 }
};


 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/deliveries/${id}/pdf`, { responseType: "blob" });
 const url = window.URL.createObjectURL(res.data);
 const a = document.createElement("a");
 a.href = url;
 a.download = `DELIVERY-${id}.pdf`;
 a.click();
 } catch (err) {
 alert("Failed to download PDF");
 }
 };


 // -----------------------
 // VIEW DETAILS MODAL
 // -----------------------
 const handleView = async (id) => {
 try {
 const res = await api.get(`/deliveries/${id}`);
 if (res.data.success) {
 setSelectedDelivery(res.data.data);
 setShowModal(true);
 }
 } catch (err) {
 alert("Failed to load delivery details");
 }
 };

 const formatDate = (date) =>
 date
 ? new Date(date).toLocaleDateString("en-US", {
 year: "numeric",
 month: "short",
 day: "numeric",
 })
 : "—";

const getStatusBadge = (status) => {
 switch (status) {
 case "Pending":
 return "warning"; // waiting for receipt

 case "Received":
 return "info"; // arrived at site

 case "Inspected":
 return "primary"; // quality check done

 case "Accepted":
 return "success"; // completed / approved

 case "Rejected":
 return "danger"; // failed inspection

 default:
 return "secondary"; // unknown / fallback
 }
};

 if (loading) return <p>Loading deliveries...</p>;
 if (error) return <p className="text-danger">{error}</p>;

 return (
 <>
 <Row className="g-4">

 {/* SUMMARY CARDS */}
 <Col md={3}>
 <Card className="shadow-sm border-0 text-center p-3">
 <h6>Total Deliveries</h6>
 <h3 className="fw-bold">{total}</h3>
 </Card>
</Col>

<Col md={3}>
 <Card className="shadow-sm border-0 text-center p-3">
 <h6>Accepted</h6>
 <h3 className="fw-bold text-success">{accepted}</h3>
 </Card>
</Col>

<Col md={3}>
 <Card className="shadow-sm border-0 text-center p-3">
 <h6>Pending</h6>
 <h3 className="fw-bold text-warning">{pending}</h3>
 </Card>
</Col>

<Col md={3}>
 <Card className="shadow-sm border-0 text-center p-3">
 <h6>Rejected</h6>
 <h3 className="fw-bold text-danger">{rejected}</h3>
 </Card>
</Col>

 {/* PIE CHART */}
 <Col md={4}>
 <Card className="shadow-sm border-0 p-3">
 <h6 className="fw-bold mb-3">Status Distribution</h6>
 <PieChart width={260} height={260}>
 <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
 {statusData.map((_, i) => (
 <Cell key={i} fill={COLORS[i % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </Card>
 </Col>

 {/* VENDOR WISE BAR */}
 <Col md={4}>
 <Card className="shadow-sm border-0 p-3">
 <h6 className="fw-bold mb-3">Vendor Deliveries</h6>
 <BarChart width={260} height={260} data={vendorData}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="vendor" />
 <YAxis />
 <Tooltip />
 <Bar dataKey="deliveries" fill="#007BFF" />
 </BarChart>
 </Card>
 </Col>

 {/* MONTHLY TREND */}
 <Col md={4}>
 <Card className="shadow-sm border-0 p-3">
 <h6 className="fw-bold mb-3">Monthly Trend</h6>
 <LineChart width={260} height={260} data={monthlyTrend}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="month" />
 <YAxis allowDecimals={false} />
 <Tooltip />
 <Line type="monotone" dataKey="count" stroke="#28A745" strokeWidth={2} />
 </LineChart>
 </Card>
 </Col>

 {/* TABLE */}
 <Col md={12}>
 <Card className="shadow-sm border-0 p-3">
 <h5 className="fw-bold mb-3">All Deliveries for Work Order</h5>

 <Table hover responsive>
 <thead>
 <tr>
 <th>#</th>
 <th>Delivery No.</th>
 <th>Vendor</th>
 <th>Challan</th>
 <th>Date</th>
 <th>Status</th>
 <th>PDF</th>
 <th>View</th>
 </tr>
 </thead>
 <tbody>
 {deliveries.map((d, i) => (
 <tr key={d._id}>
 <td>{i + 1}</td>
 <td><strong>{d.deliveryNumber || "—"}</strong></td>
 <td>{d.vendor?.name || "Unknown"}</td>
 <td>{d.challanNumber || "—"}</td>
 <td>{formatDate(d.deliveryDate)}</td>
 <td><Badge className="rounded-pill" bg={getStatusBadge(d.status)}>{d.status}</Badge></td>
 <td>
 <VerifiedCopyActions
 id={d._id}
 type="deliveries"
 hasVerifiedCopy={!!d.verifiedCopy?.filePath}
 onUploadSuccess={fetchDeliveries}
 />
 </td>
 <td>
 <button className="btn btn-sm btn-outline-primary" onClick={() => handleView(d._id)}>
 <i className="fas fa-eye"></i>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card>
 </Col>
 </Row>

 {/* MODAL VIEW DETAILS */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>Delivery Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedDelivery ? (
 <>
 {/* == BASIC INFO == */}
 <Row className="mb-3">
 <Col md={6}>
 <p><strong>Delivery No:</strong> {selectedDelivery.deliveryNumber || "—"}</p>
 <p><strong>Challan No:</strong> {selectedDelivery.challanNumber || "—"}</p>
 <p><strong>Date:</strong> {formatDate(selectedDelivery.deliveryDate)}</p>
 </Col>
 <Col md={6}>
 <p><strong>Vendor:</strong> {selectedDelivery.vendor?.name || "Unknown"}</p>
 <p><strong>Status:</strong> <Badge className="rounded-pill ms-2" bg={getStatusBadge(selectedDelivery.status)}>{selectedDelivery.status}</Badge></p>
 <p><strong>Received By:</strong> {selectedDelivery.receivedBy?.name || "—"}</p>
 </Col>
 </Row>

 <hr />

 {/* == ITEMS TABLE == */}
 <h5 className="mt-3">Items</h5>
 {selectedDelivery.items?.length > 0 ? (
 <Table bordered size="sm" className="mt-2">
 <thead>
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Ordered</th>
 <th>Delivered</th>
 <th>Unit</th>
 <th>Remark</th>
 </tr>
 </thead>
 <tbody>
 {selectedDelivery.items.map((it, index) => (
 <tr key={index}>
 <td>{index + 1}</td>
 <td>{it.itemName}</td>
 <td>{it.quantityOrdered}</td>
 <td className="fw-bold text-primary">{it.quantityDelivered}</td>
 <td>{it.unit}</td>
 <td>{it.remark || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 ) : (
 <p className="text-muted">No items found</p>
 )}

 <hr />

 {/* == DOCUMENTS == */}
 <h5 className="mt-3">Documents</h5>
 <Row>
 <Col>
 <p>
 <strong>Challan:</strong>{" "}
 {selectedDelivery.challanDocument?.fileUrl ? (
 <a href={getFileUrl(selectedDelivery.challanDocument.fileUrl)} target="_blank" rel="noopener noreferrer">View</a>
 ) : (
 "—"
 )}
 </p>
 </Col>
 <Col>
 <p>
 <strong>Test Certificate:</strong>{" "}
 {selectedDelivery.testCertificate?.fileUrl ? (
 <a href={getFileUrl(selectedDelivery.testCertificate.fileUrl)} target="_blank" rel="noopener noreferrer">View</a>
 ) : (
 "—"
 )}
 </p>
 </Col>
 <Col>
 <p>
 <strong>Other related doc:</strong>{" "}
 {selectedDelivery.clientStamp?.fileUrl ? (
 <a href={getFileUrl(selectedDelivery.clientStamp.fileUrl)} target="_blank" rel="noopener noreferrer">View</a>
 ) : (
 "—"
 )}
 </p>
 </Col>
 </Row>
 </>
) : (
 <p>Loading…</p>
)}

 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 <Button variant="primary" onClick={() => handlePreviewPDF(selectedDelivery._id)}>
 Preview PDF
 </Button>
 </Modal.Footer>
 </Modal>

 {previewingPdf && (
 <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}>
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing PDF preview…</h6>
 </div>
 </div>
 )}
 </>
 );
};

export default DeliveryTab;
